import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as FormikForm, Formik } from 'formik';
import { Box, Button, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useNotify } from 'hooks';

import {
  LeaderboardConfigFragment,
  LeaderboardConfigInput,
  SegmentStatus,
  useCreateUpdateLeaderboardConfigMutation,
  useGetLeaderboardConfigsQuery,
  useGetUserSegmentsQuery,
} from 'api';
import { Capability, Loaders } from 'components';
import { AdminLeaderboardsCreate } from 'Links';

import { LeaderboardSchema } from 'features/admin/validations';
import { useUpdateLeaderboard } from 'features/admin/updaters';
import { Leaderboard } from 'features/admin/components/Leaderboards';

const styles = {
  button: {
    marginLeft: 2,
    backgroundColor: 'common.white',
    color: 'common.black',
    '&:hover': {
      backgroundColor: 'grey[200]',
    },
  },
} as const;

const LeaderboardsIndex: FC = () => {
  const { t } = useTranslation();
  const notify = useNotify();

  const mutationOptions = useUpdateLeaderboard();
  const { mutate, isLoading } = useCreateUpdateLeaderboardConfigMutation({
    onSuccess: editNotify,
    ...mutationOptions,
  });

  const leaderboardConfigsQueryResult = useGetLeaderboardConfigsQuery(null, {
    onError: notify.queryError,
  });

  const segmentsQueryResult = useGetUserSegmentsQuery(
    { segment_status: SegmentStatus.Active },
    {
      onError: notify.queryError,
    }
  );

  function getEditValues(values: LeaderboardConfigInput) {
    const { id, leaderboard_status, description, segment_ids, start_at, end_at, title } = values;
    return { id, leaderboard_status, description, segment_ids, start_at, end_at, title };
  }

  function editNotify() {
    const message = t('components:savedSuccessfully');
    notify.info({ message });
  }

  function handleEdit(config: LeaderboardConfigFragment, values: LeaderboardConfigInput) {
    mutate({ data: values });
  }

  function renderConfig(config: LeaderboardConfigFragment) {
    return (
      <FormikForm>
        <Leaderboard
          segments={segmentsQueryResult.data.getUserSegments}
          config={config}
          working={isLoading}
        />
      </FormikForm>
    );
  }

  function renderForm() {
    const { getLeaderboardConfigs } = leaderboardConfigsQueryResult.data;

    return (
      <Grid container direction="column" spacing={3} wrap="nowrap">
        {getLeaderboardConfigs.map(config => (
          <Grid key={config.id}>
            <Formik
              initialValues={getEditValues(config)}
              onSubmit={values => handleEdit(config, values)}
              validationSchema={LeaderboardSchema(config.all_users)}
            >
              {renderConfig(config)}
            </Formik>
          </Grid>
        ))}
      </Grid>
    );
  }

  if (leaderboardConfigsQueryResult.isLoading || segmentsQueryResult.isLoading) {
    return <Loaders.Standard />;
  }

  return (
    <Fragment>
      <Box marginBottom={3}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid>
            <Typography variant="subtitle2">{t('common:leaderboard_plural')}</Typography>
          </Grid>

          <Capability name="Admin.Leaderboards.Function.CreateOtherLeaderboards">
            <Grid>
              <Button
                variant="contained"
                size="large"
                startIcon={<StarIcon />}
                component={AdminLeaderboardsCreate}
                sx={styles.button}
              >
                {t('admin:leaderboardsCreate')}
              </Button>
            </Grid>
          </Capability>
        </Grid>
      </Box>

      {renderForm()}
    </Fragment>
  );
};

export default LeaderboardsIndex;
